/**
 * Accordion Component
 * 
 * Creates an accessible accordion with expandable/collapsible sections.
 *
 * Attributes:
 * - data-accordion: Parent container for the accordion items.
 * - data-accordion-toggle: Button to toggle an accordion item's open/close state.
 * - data-accordion-arrow: Arrow element to visually indicate state.
 * - data-accordion-body: Content container that expands/collapses.
 *
 * Behavior:
 * - Clicking a toggle button expands its corresponding content and collapses others.
 * - Dynamically adjusts the max-height of content for smooth transitions.
 * - Updates aria-expanded for accessibility.
 *
 * Example Usage:
 * new Accordion('[data-accordion]');
 */
export default class Accordion {
	constructor(container) {
		this.container = container;
		this.toggles = this.container.querySelectorAll('[data-accordion-toggle]');
		this.init();
	}

	init() {
		this.toggles.forEach((toggle) => {
			toggle.addEventListener('click', () => this.toggleAccordion(toggle));
		});
	}

	toggleAccordion(toggle) {
		const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
		const body = this.container.querySelector(`#${toggle.getAttribute('aria-controls')}`);
		const arrow = toggle.querySelector('[data-accordion-arrow]');

		// Close all toggles within this container
		this.toggles.forEach((otherToggle) => {
			const otherBody = this.container.querySelector(`#${otherToggle.getAttribute('aria-controls')}`);
			const otherArrow = otherToggle.querySelector('[data-accordion-arrow]');

			otherToggle.setAttribute('aria-expanded', 'false');
			if (otherArrow) otherArrow.style.transform = 'rotate(0deg)';
			if (otherBody) otherBody.style.maxHeight = '0';
		});

		// Expand if not already expanded
		if (!isExpanded) {
			toggle.setAttribute('aria-expanded', 'true');
			if (arrow) arrow.style.transform = 'rotate(180deg)';
			if (body) body.style.maxHeight = `${body.scrollHeight}px`;
		}
	}
}
